import useRedirect from "helpers/hooks/useRedirect";
import { keyValueFormat } from "helpers/utils/utilities";
import React from "react";
import { useSelector } from "react-redux";
import { REDUCER_TYPES } from "store";
import REQUEST from "store/api";
import { ENDPOINTS } from "store/api/endpoints";
import { serializeObject } from "store/api/utils";
import { resolveResponse } from "store/api/utils";

const useJobNetwork = () => {
  const { language, token } = useSelector((state) => state[REDUCER_TYPES.AUTH]);
  const { navigateJobNetworkJobDetails } = useRedirect();

  const headers = {
    Authorization: "Bearer " + token,
  };

  const create = async (payload) => {
    if (!payload) return;
    const body = {
      ...payload,
      language,
    };

    const onSuccess = (res) =>
      resolveResponse(
        res,
        (data) => {
          navigateJobNetworkJobDetails(data.uuid, true);
        },
        "Job created successfully!"
      );

    const client = new REQUEST();
    const res = await client.post(
      `${ENDPOINTS.MANAGE}/${ENDPOINTS.JOB_NETWORK}/${ENDPOINTS.CREATE}?language=${language}`,
      body,
      headers,
      onSuccess
    );
    return res;
  };

  const edit = async (slug, payload) => {
    if (!payload) return;
    const body = {
      ...payload,
      _method: "PUT",
      language,
    };

    const onSuccess = (res) =>
      resolveResponse(
        res,
        (data) => {
          navigateJobNetworkJobDetails(data.uuid, true);
        },
        "Job details updated successfully!"
      );

    const client = new REQUEST();
    const res = await client.post(
      `${ENDPOINTS.MANAGE}/${ENDPOINTS.JOB_NETWORK}/${slug}/${ENDPOINTS.UPDATE}?language=${language}`,
      body,
      headers,
      onSuccess
    );
    return res;
  };

  const fetchJobs = async (filters = {}, isPublic) => {
    const onSuccess = (res) => resolveResponse(res, null, null, null, true);
    const client = new REQUEST();
    const res = await client.get(
      `${isPublic ? ENDPOINTS.PUBLIC : ENDPOINTS.MANAGE}/${
        ENDPOINTS.JOB_NETWORK
      }/jobs?language=${language}&${serializeObject(filters)}`,
      {},
      headers,
      onSuccess
    );
    if (res?.data?.data) return res?.data?.data;
    return [];
  };

  const fetchApplications = async (uuid, filters = {}) => {
    const onSuccess = (res) => resolveResponse(res, null, null, null, true);
    const client = new REQUEST();
    const res = await client.get(
      `${ENDPOINTS.MANAGE}/${
        ENDPOINTS.JOB_NETWORK
      }/${uuid}/applications?language=${language}&${serializeObject(filters)}`,
      {},
      headers,
      onSuccess
    );
    if (res?.data?.data) return res?.data?.data;
    return [];
  };

  const fetchJob = async (slug, isPrivate) => {
    if (!slug) return;
    const onSuccess = (res) => resolveResponse(res, null, null, null, true);
    const client = new REQUEST();
    const res = await client.get(
      `${isPrivate ? ENDPOINTS.MANAGE : ENDPOINTS.PUBLIC}/${
        ENDPOINTS.JOB_NETWORK
      }/${slug}?language=${language}`,
      {},
      headers,
      onSuccess,
      Function.prototype,
      true
    );
    if (res?.data?.data) {
      return res?.data?.data;
    }
    return res;
  };
  const fetchJobSelectList = async (filters = {}) => {
    const onSuccess = (res) => resolveResponse(res, null, null, null, true);
    const client = new REQUEST();
    const res = await client.get(
      `${ENDPOINTS.MANAGE}/${
        ENDPOINTS.JOB_NETWORK
      }/jobs-list?language=${language}&${serializeObject(filters)}`,
      {},
      headers,
      onSuccess
    );

    if (res?.data?.data) return keyValueFormat(res?.data?.data);
    return [];
  };

  const updateApplicationStatus = async (uuid, status) => {
    if (!uuid || !status) return;
    const client = new REQUEST();
    const res = await client.post(
      `${ENDPOINTS.MANAGE}/${ENDPOINTS.JOB_NETWORK}/${uuid}/application-status?language=${language}&status=${status}`,
      {},
      headers,
      resolveResponse
    );
    return res;
  };

  const applyJob = async (uuid) => {
    if (!uuid) return;
    const client = new REQUEST();
    const res = await client.post(
      `${ENDPOINTS.PUBLIC}/${ENDPOINTS.JOB_NETWORK}/${uuid}/apply?language=${language}`,
      {},
      headers,
      resolveResponse
    );
    return res;
  };

  const acceptOffer = async (uuid) => {
    if (!uuid) return;
    const client = new REQUEST();
    const res = await client.post(
      `${ENDPOINTS.PUBLIC}/${ENDPOINTS.JOB_NETWORK}/${uuid}/accept?language=${language}`,
      {},
      headers,
      resolveResponse
    );
    return res;
  };

  const rejectOffer = async (uuid) => {
    if (!uuid) return;
    const client = new REQUEST();
    const res = await client.post(
      `${ENDPOINTS.PUBLIC}/${ENDPOINTS.JOB_NETWORK}/${uuid}/reject?language=${language}`,
      {},
      headers,
      resolveResponse
    );
    return res;
  };

  const deleteJob = async (uuid) => {
    if (!uuid) return;
    const client = new REQUEST();
    await client.delete(
      `${ENDPOINTS.MANAGE}/${ENDPOINTS.JOB_NETWORK}/${uuid}/${ENDPOINTS.DELETE}?language=${language}`,
      headers,
      resolveResponse
    );
  };

  const fetchMyJobs = async (filters = {}) => {
    const onSuccess = (res) => resolveResponse(res, null, null, null, true);
    const client = new REQUEST();
    const res = await client.get(
      `${ENDPOINTS.PUBLIC}/${
        ENDPOINTS.JOB_NETWORK
      }/applied-list?language=${language}&${serializeObject(filters)}`,
      {},
      headers,
      onSuccess
    );
    if (res?.data?.data) return res?.data?.data;
    return res;
  };

  return {
    create,
    edit,
    fetchJob,
    fetchJobs,
    fetchApplications,
    fetchJobSelectList,
    updateApplicationStatus,
    deleteJob,
    applyJob,
    fetchMyJobs,
    acceptOffer,
    rejectOffer
  };
};

export default useJobNetwork;
